import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header, Footer, PostCard } from '../../components';
import storage from '../../utils/storage';
import { getArchiveInfo, getTagStats } from '../../utils/date';
import './Archive.css';

const Archive = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [archives, setArchives] = useState([]);
  const [tagStats, setTagStats] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [viewMode, setViewMode] = useState('date'); // 'date', 'tag', 'all'

  useEffect(() => {
    loadData();
    parseQueryParams();
  }, [location.search]);

  useEffect(() => {
    filterPosts();
  }, [selectedDate, selectedTag, posts]);

  const loadData = () => {
    try {
      const postsData = storage.getPosts();
      const usersData = storage.getUsers();
      
      // 按创建时间排序
      const sortedPosts = postsData.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setPosts(sortedPosts);
      setUsers(usersData);
      setArchives(getArchiveInfo(sortedPosts));
      setTagStats(getTagStats(sortedPosts));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const parseQueryParams = () => {
    const params = new URLSearchParams(location.search);
    const date = params.get('date');
    const tag = params.get('tag');
    
    if (date) {
      setSelectedDate(date);
      setSelectedTag(null);
      setViewMode('date');
    } else if (tag) {
      setSelectedTag(tag);
      setSelectedDate(null);
      setViewMode('tag');
    } else {
      setSelectedDate(null);
      setSelectedTag(null);
      setViewMode('all');
    }
  };

  const filterPosts = () => {
    let filtered = posts;
    
    if (selectedDate) {
      filtered = posts.filter(post => {
        const postDate = new Date(post.createdAt);
        const year = postDate.getFullYear();
        const month = String(postDate.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}` === selectedDate;
      });
    } else if (selectedTag) {
      filtered = posts.filter(post => 
        post.tags && post.tags.includes(selectedTag)
      );
    }
    
    setFilteredPosts(filtered);
  };

  const getAuthor = (authorId) => {
    return users.find(user => user.id === authorId);
  };

  const handleDateFilter = (date) => {
    navigate(`/archive?date=${date}`);
  };

  const handleTagFilter = (tag) => {
    navigate(`/archive?tag=${tag}`);
  };

  const clearFilter = () => {
    navigate('/archive');
  };

  const getCurrentFilterTitle = () => {
    if (selectedDate) {
      const [year, month] = selectedDate.split('-');
      return `${year}年${month}月的文章`;
    } else if (selectedTag) {
      return `标签: ${selectedTag}`;
    }
    return '所有文章';
  };

  return (
    <div className="app">
      <Header />
      <main className="main">
        <div className="container">
          <div className="archive-page">
            {/* 页面标题 */}
            <div className="archive-header">
              <h1>文章归档</h1>
              <p className="archive-subtitle">
                {getCurrentFilterTitle()} ({filteredPosts.length}篇)
              </p>
              {(selectedDate || selectedTag) && (
                <button onClick={clearFilter} className="clear-filter-btn">
                  清除筛选
                </button>
              )}
            </div>

            <div className="archive-content">
              {/* 侧边栏 */}
              <aside className="archive-sidebar">
                {/* 归档导航 */}
                <div className="archive-nav">
                  <button
                    className={`archive-nav-btn ${viewMode === 'all' ? 'active' : ''}`}
                    onClick={clearFilter}
                  >
                    全部文章
                  </button>
                  <button
                    className={`archive-nav-btn ${viewMode === 'date' ? 'active' : ''}`}
                    onClick={() => setViewMode('date')}
                  >
                    按日期
                  </button>
                  <button
                    className={`archive-nav-btn ${viewMode === 'tag' ? 'active' : ''}`}
                    onClick={() => setViewMode('tag')}
                  >
                    按标签
                  </button>
                </div>

                {/* 日期归档 */}
                {viewMode === 'date' && (
                  <div className="archive-section">
                    <h3>按日期归档</h3>
                    <ul className="archive-date-list">
                      {archives.map((archive) => (
                        <li key={archive.key} className="archive-date-item">
                          <button
                            onClick={() => handleDateFilter(archive.key)}
                            className={`archive-date-link ${
                              selectedDate === archive.key ? 'active' : ''
                            }`}
                          >
                            <span>
                              {archive.year}年{archive.month}月
                            </span>
                            <span className="archive-count">
                              {archive.count}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 标签归档 */}
                {viewMode === 'tag' && (
                  <div className="archive-section">
                    <h3>按标签归档</h3>
                    <div className="archive-tag-list">
                      {tagStats.map((tag) => (
                        <button
                          key={tag.name}
                          onClick={() => handleTagFilter(tag.name)}
                          className={`archive-tag-item ${
                            selectedTag === tag.name ? 'active' : ''
                          }`}
                        >
                          {tag.name}
                          <span className="tag-count">{tag.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 统计信息 */}
                <div className="archive-section">
                  <h3>统计信息</h3>
                  <div className="archive-stats">
                    <div className="stat-item">
                      <span className="stat-label">文章总数</span>
                      <span className="stat-value">{posts.length}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">标签总数</span>
                      <span className="stat-value">{tagStats.length}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">归档月份</span>
                      <span className="stat-value">{archives.length}</span>
                    </div>
                  </div>
                </div>
              </aside>

              {/* 文章列表 */}
              <div className="archive-posts">
                {filteredPosts.length === 0 ? (
                  <div className="empty-state">
                    <h3>暂无文章</h3>
                    <p>该分类下还没有文章</p>
                  </div>
                ) : (
                  <div className="posts-grid">
                    {filteredPosts.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        author={getAuthor(post.author)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Archive;